/**
 * RichTextEditor Component
 * 
 * WYSIWYG rich text editor using TipTap for editing HTML content.
 * Supports bold, italic, bullet lists, numbered lists, and links.
 */

import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import './RichTextEditor.scss';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  label?: string;
  hint?: string;
  disabled?: boolean;
}

const MenuBar: React.FC<{ editor: ReturnType<typeof useEditor> }> = ({ editor }) => {
  const setLink = useCallback(() => {
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);

    // Cancelled
    if (url === null) {
      return;
    }

    // Empty - remove link
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // Update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="rich-text-editor__toolbar">
      <div className="rich-text-editor__toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={`rich-text-editor__toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={`rich-text-editor__toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
          title="Italic"
        >
          <em>I</em>
        </button>
      </div>

      <div className="rich-text-editor__toolbar-divider" />

      <div className="rich-text-editor__toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`rich-text-editor__toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
          title="Bullet List"
        >
          •
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`rich-text-editor__toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
          title="Numbered List"
        >
          1.
        </button>
      </div>

      <div className="rich-text-editor__toolbar-divider" />

      <div className="rich-text-editor__toolbar-group">
        <button
          type="button"
          onClick={setLink}
          className={`rich-text-editor__toolbar-btn ${editor.isActive('link') ? 'active' : ''}`}
          title="Add Link"
        >
          🔗
        </button>
        {editor.isActive('link') && (
          <button
            type="button"
            onClick={() => editor.chain().focus().unsetLink().run()}
            className="rich-text-editor__toolbar-btn"
            title="Remove Link"
          >
            ✕
          </button>
        )}
      </div>

      <div className="rich-text-editor__toolbar-divider" />

      <div className="rich-text-editor__toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          className="rich-text-editor__toolbar-btn"
          title="Undo"
        >
          ↶
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          className="rich-text-editor__toolbar-btn"
          title="Redo"
        >
          ↷
        </button>
      </div>
    </div>
  );
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  label,
  hint,
  disabled = false,
}) => {
  const editor = useEditor(
    {
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }),
      ],
      content: value || '',
      editable: !disabled,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        // TipTap returns <p></p> for empty content, normalize to empty string
        const normalizedHtml = html === '<p></p>' ? '' : html;
        onChange(normalizedHtml);
      },
    },
    [] // Empty deps - only create editor once
  );

  // Sync external value changes
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Avoid unnecessary updates
      const currentHtml = editor.getHTML();
      const normalizedCurrent = currentHtml === '<p></p>' ? '' : currentHtml;
      if (value !== normalizedCurrent) {
        editor.commands.setContent(value || '');
      }
    }
  }, [value, editor]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  return (
    <div className={`rich-text-editor ${disabled ? 'rich-text-editor--disabled' : ''}`}>
      {label && (
        <label className="rich-text-editor__label">{label}</label>
      )}
      <div className="rich-text-editor__container">
        <MenuBar editor={editor} />
        <EditorContent 
          editor={editor} 
          className="rich-text-editor__content"
          placeholder={placeholder}
        />
      </div>
      {hint && (
        <span className="rich-text-editor__hint">{hint}</span>
      )}
    </div>
  );
};

export default RichTextEditor;
